import {
  center,
  formatQtyPrice,
  formatReceiptDate,
  formatReceiptTime,
  formatThermalAmount,
  formatThermalCurrency,
  joinColumns,
  normalizeText,
  pairLine,
  separatorLine,
  wrapText
} from './thermalReceiptFormatter';
import { getReceiptLabel, isReceiptRtl } from '../../utils/receiptLabels';

function t(key, language) {
  return getReceiptLabel(key, language);
}

function padRightLocal(text, width) {
  const value = normalizeText(text);
  if (value.length >= width) return value.slice(0, width);
  return value + ' '.repeat(width - value.length);
}

function padQtyPrice(qty, unitPrice, width, config) {
  const raw = formatQtyPrice(qty, unitPrice, config);
  if (raw.length >= width) return raw.slice(-width);
  return ' '.repeat(width - raw.length) + raw;
}

function buildItemLines(item, config) {
  const { profile } = config;
  const { name: nameWidth, qtyPrice: qtyPriceWidth, total: totalWidth } = profile.columns;
  const qtyPrice = padQtyPrice(item.quantity, item.unitPrice, qtyPriceWidth, config);
  const total = formatThermalAmount(item.lineTotal, config);
  const nameLines = wrapText(item.name, nameWidth);

  if (nameLines.length === 1) {
    return [
      joinColumns(
        [nameLines[0], qtyPrice, total],
        [nameWidth, qtyPriceWidth, totalWidth],
        ['left', 'right', 'right']
      )
    ];
  }

  const lines = [padRightLocal(nameLines[0], profile.charWidth)];
  for (let i = 1; i < nameLines.length - 1; i += 1) {
    lines.push(padRightLocal(nameLines[i], profile.charWidth));
  }

  lines.push(
    joinColumns(
      [nameLines[nameLines.length - 1], qtyPrice, total],
      [nameWidth, qtyPriceWidth, totalWidth],
      ['left', 'right', 'right']
    )
  );

  return lines;
}

function buildHeaderTable(config) {
  const { profile, language } = config;
  const { name, qtyPrice, total } = profile.columns;
  const qtyWidth = Math.max(3, Math.floor(qtyPrice * 0.35));
  const priceWidth = Math.max(4, qtyPrice - qtyWidth);

  return joinColumns(
    [t('article', language), t('qty', language), t('price', language), t('total', language)],
    [name, qtyWidth, priceWidth, total],
    ['left', 'center', 'right', 'right']
  );
}

/**
 * Builds plain-text thermal receipt lines (supermarket style).
 */
export function buildThermalReceiptLines(viewModel, config) {
  const width = config.profile.charWidth;
  const language = config.language || 'fr';
  const rtl = isReceiptRtl(language);
  const lines = [];

  if (viewModel.flags.showLogo && viewModel.store.logoUrl) {
    lines.push(center('[LOGO]', width));
  }

  lines.push(center(normalizeText(viewModel.store.name).toUpperCase(), width));
  if (viewModel.store.address) lines.push(center(viewModel.store.address, width));
  if (viewModel.store.phone) lines.push(center(viewModel.store.phone, width));
  if (config.showNif !== false && viewModel.flags.showNif && viewModel.store.nif) {
    lines.push(center(`${t('nif', language)}: ${viewModel.store.nif}`, width));
  }

  const compact = config.compactLayout === true;
  if (!compact) lines.push('');
  lines.push(center(viewModel.receipt.title || t('receiptTitleClient', language), width));
  lines.push(pairLine(`${t('receiptNo', language)}:`, viewModel.receipt.number, width, rtl));
  if (compact) {
    const dateTime = `${formatReceiptDate(viewModel.receipt.date, config.locale)} ${formatReceiptTime(viewModel.receipt.date, config.locale)}`;
    lines.push(pairLine(`${t('date', language)}:`, dateTime, width, rtl));
  } else {
    lines.push(pairLine(`${t('date', language)}:`, formatReceiptDate(viewModel.receipt.date, config.locale), width, rtl));
    lines.push(pairLine(`${t('time', language)}:`, formatReceiptTime(viewModel.receipt.date, config.locale), width, rtl));
  }

  if (viewModel.cashier.show) {
    lines.push(pairLine(`${t('cashier', language)}:`, viewModel.cashier.name, width, rtl));
  }
  if (viewModel.customer.show && viewModel.customer.name) {
    lines.push(pairLine(`${t('customer', language)}`, viewModel.customer.name, width, rtl));
  }
  if (viewModel.location.show && viewModel.location.name) {
    lines.push(pairLine(`${t('location', language)}`, viewModel.location.name, width, rtl));
  }

  lines.push(separatorLine(width, config.lineChar));
  lines.push(buildHeaderTable(config));
  lines.push(separatorLine(width, config.lineChar));

  for (const item of viewModel.items) {
    lines.push(...buildItemLines(item, config));
  }

  lines.push(separatorLine(width, config.lineChar));

  if (viewModel.flags.showItemCount) {
    lines.push(pairLine(`${t('items', language)}:`, String(viewModel.totals.itemCount), width, rtl));
  }

  if (viewModel.totals.discount > 0) {
    lines.push(
      pairLine(`${t('discount', language)}`, `-${formatThermalCurrency(viewModel.totals.discount, config)}`, width, rtl)
    );
  }

  if (viewModel.totals.showVat && viewModel.totals.tax > 0) {
    lines.push(pairLine(`${t('vat', language)}:`, formatThermalCurrency(viewModel.totals.tax, config), width, rtl));
  }

  lines.push(
    pairLine(`${t('totalThermal', language)}:`, formatThermalCurrency(viewModel.totals.total, config), width, rtl)
  );
  lines.push(separatorLine(width, config.lineChar));
  lines.push(pairLine(`${t('payment', language)}`, viewModel.payment.method, width, rtl));

  if (viewModel.payment.isCash) {
    lines.push(pairLine(`${t('thermalCashPaid', language)}:`, formatThermalCurrency(viewModel.payment.cashAmount, config), width, rtl));
    lines.push(pairLine(`${t('thermalChange', language)}:`, formatThermalCurrency(viewModel.payment.change, config), width, rtl));
  }

  lines.push(separatorLine(width, config.lineChar));
  lines.push(center(viewModel.footerMessage, width));

  if (viewModel.flags.showBarcode && viewModel.receipt.barcode) {
    if (!compact) lines.push('');
    lines.push(center(`*${viewModel.receipt.barcode}*`, width));
  }

  if (viewModel.flags.showQrCode && viewModel.receipt.qrPayload) {
    lines.push(center('[QR]', width));
  }

  return lines.filter((line, index, all) => {
    if (line !== '') return true;
    return index === 0 || all[index - 1] !== '';
  });
}
